import {ReactNode} from "react";
import {css, Theme} from "@emotion/react";
import {Button} from "@pg-design/button";
import {borderRadius, flex, flexDirection, mb, p, pt, w100} from "@pg-design/helpers-css";
import {CheckIcon} from "@pg-design/icons";
import {Text} from "@pg-design/text";

import {flexGap, flexGrow} from "../../../../../components/css_helpers/flex";

interface ISubscriptionPlanCardProps {
    title: string;
    price: ReactNode;
    subtitle?: string;
    description?: string;
    features: string[];
    ctaLabel: string;
    onCtaClick?: () => void;
    ctaDisabled?: boolean;
    badge?: string;
    discountLabel?: string;
}

export const SubscriptionPlanCard = ({
    title,
    price,
    subtitle,
    description,
    features,
    ctaLabel,
    onCtaClick,
    ctaDisabled,
    badge,
    discountLabel
}: ISubscriptionPlanCardProps) => {
    return (
        <div css={card}>
            <Text variant="headline_5" css={mb(2)}>
                {title}
            </Text>

            <div css={priceRow}>
                {price}
                {badge && (
                    <Text variant="info_txt_2" css={trialBadge}>
                        {badge}
                    </Text>
                )}
            </div>

            {discountLabel && (
                <Text variant="info_txt_2" css={mb(2)}>
                    {discountLabel}
                </Text>
            )}

            {subtitle && (
                <Text variant="body_copy_1" css={mb(2)}>
                    {subtitle}
                </Text>
            )}

            {description && (
                <Text variant="info_txt_1" css={mb(3)}>
                    {description}
                </Text>
            )}

            <ul css={featuresList}>
                {features.map((feature) => (
                    <li css={featureItem} key={feature}>
                        <CheckIcon size="1.6" wrapperSize="1.6" wrapperColor="#1a1a1e" fill="#fff" css={checkIcon} />
                        <Text variant="body_copy_1">{feature}</Text>
                    </li>
                ))}
            </ul>

            <div css={ctaHolder}>
                <Button variant="filled_secondary" size="medium" onClick={onCtaClick} disabled={ctaDisabled}>
                    {ctaLabel}
                </Button>
            </div>
        </div>
    );
};

const card = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    ${flexGrow(1)};
    ${p(3)};
    ${borderRadius(2)};
    background-color: #fff;
    border: 1px solid ${theme.colors.gray["300"]};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    height: 100%;
    min-height: 100%;
`;

const priceRow = css`
    ${flex("center", "flex-start")};
    ${flexGap(1)};
    flex-wrap: wrap;
    ${mb(1)};
`;

const trialBadge = (theme: Theme) => css`
    ${p(0.5, 1.5)};
    ${borderRadius(4)};
    background-color: ${theme.colors.gray["200"]};
    color: ${theme.colors.gray["800"]};
    white-space: nowrap;
`;

const featuresList = css`
    list-style: none;
    margin: 0;
    padding: 0;
    ${flexGrow(1)};
`;

const featureItem = css`
    ${flex("flex-start", "flex-start")};
    ${flexGap(1)};
    ${mb(1.5)};
`;

const checkIcon = css`
    flex-shrink: 0;
    margin-top: 0.2rem;
`;

const ctaHolder = css`
    margin-top: auto;
    ${w100};
    ${pt(3)};

    button {
        width: 100%;
    }
`;
